import React from "react";
import {
  Download,
  Eye,
  FileText,
  History,
  Landmark,
  LoaderCircle,
  ReceiptText,
  Search,
  WalletCards,
  Plus,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  Info,
  AlertTriangle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import "./LenderInvoicesPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* =========================================================
   FORMATTERS
   ========================================================= */

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${formattedDate} ${formattedTime}`;
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatRenovationType(value = "") {
  if (!value) return "—";

  return String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatStatus(value = "") {
  if (!value) return "";

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isProvisionalFundingStatus(value = "") {
  return ["EXPECTED", "PLANNED", "APPLIED"].includes(
    String(value || "").toUpperCase(),
  );
}

function isConfirmedFundingStatus(value = "") {
  return ["CONFIRMED", "APPROVED", "ACTIVE"].includes(
    String(value || "").toUpperCase(),
  );
}

function isUnavailableFundingStatus(value = "") {
  return ["REJECTED", "CANCELLED", "EXHAUSTED"].includes(
    String(value || "").toUpperCase(),
  );
}

function getFundingStatusLabel(source, t) {
  const status = String(source?.status || "").toUpperCase();

  if (status === "EXPECTED" || status === "PLANNED") {
    return t("workspace.funding.status.expected", "Expected");
  }

  if (status === "APPLIED") {
    return t("workspace.funding.status.applied", "Applied");
  }

  if (status === "CONFIRMED") {
    return t("workspace.funding.status.confirmed", "Confirmed");
  }

  if (status === "APPROVED") {
    return t("workspace.funding.status.approved", "Approved");
  }

  if (status === "ACTIVE") {
    return t("workspace.funding.status.active", "Active");
  }

  if (status === "EXHAUSTED") {
    return t("workspace.funding.status.exhausted", "Exhausted");
  }

  if (status === "REJECTED") {
    return t("workspace.funding.status.rejected", "Rejected");
  }

  if (status === "CANCELLED") {
    return t("workspace.funding.status.cancelled", "Cancelled");
  }

  return source?.statusLabel || formatStatus(status) || "—";
}

function getFundingStatusClass(source) {
  const status = String(source?.status || "").toUpperCase();

  if (isProvisionalFundingStatus(status)) return "is-expected";
  if (isConfirmedFundingStatus(status)) return "is-confirmed";
  if (status === "EXHAUSTED") return "is-exhausted";
  if (["REJECTED", "CANCELLED"].includes(status)) return "is-unavailable";

  return "";
}

function getFundingSourceName(source, t) {
  if (!source) return t("workspace.funding.source", "Funding source");

  if (source.financingProduct) return source.financingProduct;
  if (source.fundingTypeLabel) return source.fundingTypeLabel;

  if (source.isCurrentLenderLoan) {
    return t("workspace.funding.lenderFinancing", "Lender financing");
  }

  return (
    source.sourceTypeCode ||
    source.fundingType ||
    t("workspace.funding.source", "Funding source")
  );
}

function getFundingSourceTotal(source) {
  const amount = Number(
    source?.renovationAmount ??
      source?.approvedAmount ??
      source?.requestedAmount ??
      0,
  );

  return Number.isFinite(amount) ? amount : 0;
}

function canLenderAllocateFromSource(source) {
  if (!source?.id || !source?.canFundInvoices) return false;
  if (isUnavailableFundingStatus(source?.status)) return false;

  const allocationRole = String(source?.allocationRole || "").toUpperCase();

  if (allocationRole && allocationRole !== "LENDER") {
    return false;
  }

  return getFundingSourceTotal(source) > 0;
}

const API_ERROR_FIELD_LABELS = {
  funding_source_id: "Funding source",
  amount: "Allocation amount",
  evidence_invoice_version_id: "Evidence version",
  eligibility_confirmed: "Eligibility",
  eligibility_notes: "Eligibility notes",
  invoice: "Invoice",
  non_field_errors: "Validation",
};

function collectApiErrorDetails(value, path = "") {
  if (value === null || value === undefined || value === "") return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectApiErrorDetails(item, path));
  }

  if (typeof value === "object") {
    return Object.entries(value).flatMap(([key, child]) =>
      collectApiErrorDetails(child, path ? `${path}.${key}` : key),
    );
  }

  const rawField = path.split(".")[0] || "";
  const field = API_ERROR_FIELD_LABELS[rawField] || rawField.replace(/_/g, " ");

  return [
    {
      field,
      message: String(value),
    },
  ];
}

function buildApiError(data, fallback) {
  const details = collectApiErrorDetails(data?.errors);
  const backendDetail =
    typeof data?.detail === "string" && data.detail.trim()
      ? data.detail.trim()
      : "";

  const error = new Error(details[0]?.message || backendDetail || fallback);

  error.title = backendDetail || fallback;
  error.details = details;

  return error;
}

/* =========================================================
   API MAPPING
   ========================================================= */

function mapVersionFromApi(version = {}) {
  const versionNumber = version.versionNumber ?? version.version_number ?? 1;

  return {
    id: version.id,
    versionNumber,
    filename:
      version.filename ??
      version.originalFilename ??
      version.original_filename ??
      "",
    amount: version.amount ?? null,
    notes: version.notes ?? "",
    uploadedByName: version.uploadedByName ?? version.uploaded_by_name ?? "",
    uploadedAt: version.uploadedAt ?? version.uploaded_at ?? null,
    isLatest: Boolean(version.isLatest ?? version.is_latest),
  };
}

function mapFundingAllocationFromApi(allocation = {}) {
  return {
    id: allocation.id ?? null,
    fundingSourceId:
      allocation.fundingSourceId ?? allocation.funding_source_id ?? null,
    sourceTypeId: allocation.sourceTypeId ?? allocation.source_type_id ?? null,
    sourceTypeCode:
      allocation.sourceTypeCode ?? allocation.source_type_code ?? "",
    fundingType: allocation.fundingType ?? allocation.funding_type ?? "",
    fundingTypeLabel:
      allocation.fundingTypeLabel ?? allocation.funding_type_label ?? "",
    financingProduct:
      allocation.financingProduct ?? allocation.financing_product ?? "",
    providerName: allocation.providerName ?? allocation.provider_name ?? "",
    isCurrentLenderLoan: Boolean(
      allocation.isCurrentLenderLoan ?? allocation.is_current_lender_loan,
    ),
    amount: allocation.amount ?? "0.00",
    currency: allocation.currency ?? "EUR",
    createdByUserId:
      allocation.createdByUserId ?? allocation.created_by_user_id ?? null,
    operatorRole: allocation.operatorRole ?? allocation.operator_role ?? "",
    actingRole: allocation.actingRole ?? allocation.acting_role ?? "",
    actingOrganisationId:
      allocation.actingOrganisationId ??
      allocation.acting_organisation_id ??
      null,
    actingOrganisationName:
      allocation.actingOrganisationName ??
      allocation.acting_organisation_name ??
      "",
    eligibilityConfirmed: Boolean(
      allocation.eligibilityConfirmed ?? allocation.eligibility_confirmed,
    ),
    eligibilityNotes:
      allocation.eligibilityNotes ?? allocation.eligibility_notes ?? "",
    evidenceInvoiceVersionId:
      allocation.evidenceInvoiceVersionId ??
      allocation.evidence_invoice_version_id ??
      null,
  };
}

function mapFundingSourceFromApi(source = {}) {
  return {
    id: source.id ?? null,
    sourceTypeId: source.sourceTypeId ?? source.source_type_id ?? null,
    sourceTypeCode: source.sourceTypeCode ?? source.source_type_code ?? "",
    fundingType: source.fundingType ?? source.funding_type ?? "",
    fundingTypeLabel:
      source.fundingTypeLabel ?? source.funding_type_label ?? "",
    canFundInvoices: Boolean(
      source.canFundInvoices ?? source.can_fund_invoices,
    ),
    allocationRole: source.allocationRole ?? source.allocation_role ?? "",
    allocationRules: source.allocationRules ?? source.allocation_rules ?? {},
    financingProduct: source.financingProduct ?? source.financing_product ?? "",
    providerOrganisationId:
      source.providerOrganisationId ?? source.provider_organisation_id ?? null,
    providerName: source.providerName ?? source.provider_name ?? "",
    relatedLoanCaseId:
      source.relatedLoanCaseId ?? source.related_loan_case_id ?? null,
    relatedSubsidyGrantId:
      source.relatedSubsidyGrantId ?? source.related_subsidy_grant_id ?? null,
    isCurrentLenderLoan: Boolean(
      source.isCurrentLenderLoan ?? source.is_current_lender_loan,
    ),
    requestedAmount: source.requestedAmount ?? source.requested_amount ?? null,
    approvedAmount: source.approvedAmount ?? source.approved_amount ?? null,
    purchaseAmount: source.purchaseAmount ?? source.purchase_amount ?? null,
    renovationAmount:
      source.renovationAmount ?? source.renovation_amount ?? null,
    allocatedAmount:
      source.allocatedAmount ?? source.allocated_amount ?? "0.00",
    remainingAmount:
      source.remainingAmount ?? source.remaining_amount ?? "0.00",
    currency: source.currency ?? "EUR",
    status: source.status ?? "",
    statusLabel: source.statusLabel ?? source.status_label ?? "",
    externalReference:
      source.externalReference ?? source.external_reference ?? "",
    notes: source.notes ?? "",
  };
}

function mapInvoiceFromApi(invoice = {}) {
  const versions = Array.isArray(invoice.versions)
    ? invoice.versions
        .map(mapVersionFromApi)
        .sort((a, b) => b.versionNumber - a.versionNumber)
    : [];

  const latestVersionNumber =
    invoice.latestVersionNumber ??
    invoice.latest_version ??
    versions[0]?.versionNumber ??
    1;

  let latestVersion =
    versions.find((version) => version.versionNumber === latestVersionNumber) ||
    versions.find((version) => version.isLatest) ||
    versions[0] ||
    null;

  if (
    !latestVersion &&
    invoice.latestVersion &&
    typeof invoice.latestVersion === "object"
  ) {
    latestVersion = mapVersionFromApi(invoice.latestVersion);
  }

  const renovationType =
    invoice.renovationType ?? invoice.renovation_type ?? "";

  const fundingAllocations = Array.isArray(invoice.fundingAllocations)
    ? invoice.fundingAllocations.map(mapFundingAllocationFromApi)
    : Array.isArray(invoice.funding_allocations)
      ? invoice.funding_allocations.map(mapFundingAllocationFromApi)
      : [];

  return {
    id: invoice.id,
    renovationType,
    renovationTypeLabel:
      invoice.renovationTypeLabel ??
      invoice.renovation_type_label ??
      formatRenovationType(renovationType),
    filename:
      invoice.latestOriginalFilename ??
      invoice.latest_original_filename ??
      invoice.filename ??
      latestVersion?.filename ??
      "",
    amount:
      invoice.latestAmount ??
      invoice.latest_amount ??
      invoice.amount ??
      latestVersion?.amount ??
      null,
    notes:
      invoice.latestNotes ?? invoice.latest_notes ?? latestVersion?.notes ?? "",
    uploadedAt:
      invoice.latestUploadedAt ??
      invoice.latest_uploaded_at ??
      invoice.uploadedAt ??
      latestVersion?.uploadedAt ??
      null,
    latestVersionNumber,
    versionCount:
      invoice.versionCount ?? invoice.version_count ?? versions.length,
    latestVersion,
    versions,
    allocatedAmount:
      invoice.allocatedAmount ?? invoice.allocated_amount ?? "0.00",
    unallocatedAmount:
      invoice.unallocatedAmount ??
      invoice.unallocated_amount ??
      invoice.amount ??
      latestVersion?.amount ??
      "0.00",
    fundingAllocations,
    createdAt: invoice.createdAt ?? invoice.created_at ?? null,
    updatedAt: invoice.updatedAt ?? invoice.updated_at ?? null,
  };
}

function mapFinancialSummary(data = {}) {
  const financial = data.financialSummary ?? data.financial_summary ?? {};
  const summary = data.summary ?? {};

  const renovationFinancing =
    financial.renovationFinancing ??
    financial.renovation_financing ??
    data.renovationFinancing ??
    data.renovation_financing ??
    summary.renovationFinancing ??
    summary.renovation_financing ??
    null;

  const totalInvoiced =
    financial.totalInvoiced ??
    financial.total_invoiced ??
    data.totalInvoiced ??
    data.total_invoiced ??
    summary.totalInvoiced ??
    summary.total_invoiced ??
    "0.00";

  const totalFundingAllocated =
    financial.totalFundingAllocated ??
    financial.total_funding_allocated ??
    "0.00";

  const invoiceUnallocatedAmount =
    financial.invoiceUnallocatedAmount ??
    financial.invoice_unallocated_amount ??
    "0.00";

  const lenderLoanAllocated =
    financial.lenderLoanAllocated ?? financial.lender_loan_allocated ?? "0.00";

  const lenderLoanRemaining =
    financial.lenderLoanRemaining ??
    financial.lender_loan_remaining ??
    financial.remainingAmount ??
    financial.remaining_amount ??
    null;

  const invoiceCount =
    financial.invoiceCount ??
    financial.invoice_count ??
    data.invoiceCount ??
    data.invoice_count ??
    summary.invoiceCount ??
    summary.invoice_count ??
    0;

  const financingLabel =
    financial.financingLabel ??
    financial.financing_label ??
    data.financingLabel ??
    data.financing_label ??
    "Lender renovation financing";

  return {
    renovationFinancing,
    totalInvoiced,
    totalFundingAllocated,
    invoiceUnallocatedAmount,
    lenderLoanAllocated,
    lenderLoanRemaining,
    remainingAmount: lenderLoanRemaining,
    invoiceCount,
    financingLabel,
  };
}

function mapLoanCaseFromApi(data = {}) {
  const loanCase = data.loanCase ?? data.loan_case ?? data;
  const projectData = data.project ?? loanCase.project ?? null;

  const project = projectData
    ? {
        id: projectData.id ?? projectData.project_id ?? null,
        code: projectData.code ?? projectData.project_code ?? "",
        title: projectData.title ?? projectData.project_title ?? "",
        status:
          projectData.statusLabel ??
          projectData.status_label ??
          projectData.status ??
          "",
      }
    : null;

  const contextType =
    data.contextType ??
    data.context_type ??
    loanCase.contextType ??
    loanCase.context_type ??
    (project ? "PROJECT" : "LOAN_CASE");

  const fundingSources = Array.isArray(data.fundingSources)
    ? data.fundingSources.map(mapFundingSourceFromApi)
    : Array.isArray(data.funding_sources)
      ? data.funding_sources.map(mapFundingSourceFromApi)
      : [];

  const lenderFundingSourceRaw =
    data.lenderFundingSource ?? data.lender_funding_source ?? null;

  const lenderFundingSource = lenderFundingSourceRaw
    ? mapFundingSourceFromApi(lenderFundingSourceRaw)
    : (fundingSources.find((source) => source.isCurrentLenderLoan) ?? null);

  return {
    loanCaseId:
      loanCase.loanCaseId ??
      loanCase.loan_case_id ??
      data.loanCaseId ??
      data.loan_case_id ??
      loanCase.id ??
      data.id,
    caseType:
      loanCase.caseType ??
      loanCase.case_type ??
      data.caseType ??
      data.case_type ??
      "",
    parentLoanCaseId:
      loanCase.parentLoanCaseId ??
      loanCase.parent_loan_case_id ??
      data.parentLoanCaseId ??
      data.parent_loan_case_id ??
      null,
    caseReference:
      loanCase.caseReference ??
      loanCase.case_reference ??
      data.caseReference ??
      data.case_reference ??
      "",
    homeowner:
      data.homeowner ??
      data.homeownerName ??
      data.homeowner_name ??
      loanCase.homeowner ??
      loanCase.homeownerName ??
      loanCase.homeowner_name ??
      "",
    status:
      data.statusLabel ??
      data.status_label ??
      loanCase.statusLabel ??
      loanCase.status_label ??
      loanCase.status ??
      "",
    source: loanCase.source ?? data.source ?? "",
    contextType,
    project,
    lastUpdate:
      data.lastUpdate ??
      data.last_update ??
      data.updatedAt ??
      data.updated_at ??
      loanCase.updatedAt ??
      loanCase.updated_at ??
      null,
    financialSummary: mapFinancialSummary(data),
    fundingSources,
    lenderFundingSource,
    invoices: Array.isArray(data.invoices)
      ? data.invoices.map(mapInvoiceFromApi)
      : Array.isArray(data.results)
        ? data.results.map(mapInvoiceFromApi)
        : [],
  };
}

/* =========================================================
   LOAN CASE SIDEBAR
   ========================================================= */

function LoanCasesPanel({
  loanCases,
  selectedLoanCaseId,
  search,
  onSearchChange,
  onSelect,
  loading,
  t,
}) {
  return (
    <section className="lender-invoices-cases">
      <div className="lender-invoices-panel-header">
        <div>
          <span className="lender-invoices-panel-kicker">
            <Landmark size={14} />
            {t("workspace.cases.kicker", "Loan cases")}
          </span>

          <h2>{t("workspace.cases.title", "Renovation loan cases")}</h2>
        </div>

        <span className="lender-invoices-count-pill">{loanCases.length}</span>
      </div>

      <label className="lender-invoices-search">
        <Search size={16} />
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t(
            "workspace.cases.search",
            "Search case, project or homebuyer...",
          )}
        />
      </label>

      <div className="lender-invoices-case-list">
        {loading ? (
          <div className="lender-invoices-state">
            <LoaderCircle size={20} className="lender-invoices-spin" />
            <span>{t("workspace.cases.loading", "Loading loan cases...")}</span>
          </div>
        ) : loanCases.length === 0 ? (
          <div className="lender-invoices-state">
            <Landmark size={21} />
            <strong>
              {t("workspace.cases.emptyTitle", "No loan cases found")}
            </strong>
            <span>
              {t(
                "workspace.cases.emptyText",
                "No renovation loan cases match this search.",
              )}
            </span>
          </div>
        ) : (
          loanCases.map((loanCase) => {
            const selected =
              String(loanCase.loanCaseId) === String(selectedLoanCaseId);

            return (
              <button
                key={loanCase.loanCaseId}
                type="button"
                className={`lender-invoices-case-card ${
                  selected ? "is-selected" : ""
                }`}
                onClick={() => onSelect(loanCase.loanCaseId)}
              >
                <div className="lender-invoices-case-card__top">
                  <div>
                    <strong>
                      {loanCase.caseReference || `#${loanCase.loanCaseId}`}
                    </strong>
                    <span>
                      {loanCase.homeowner || t("common.homebuyer", "Homebuyer")}
                    </span>
                  </div>

                  <span className="lender-invoices-case-card__invoice-count">
                    {loanCase.financialSummary?.invoiceCount ?? 0}
                  </span>
                </div>

                <div className="lender-invoices-case-card__context">
                  {loanCase.project?.code ? (
                    <span className="is-project">
                      {loanCase.project.code} ·{" "}
                      {t(
                        "workspace.cases.projectContext",
                        "Renovation project",
                      )}
                    </span>
                  ) : (
                    <span>
                      {t(
                        "workspace.cases.loanCaseContext",
                        "Loan case renovation tracking",
                      )}
                    </span>
                  )}
                </div>

                <div className="lender-invoices-case-card__money">
                  <div>
                    <span>{t("workspace.cases.invoiced", "Invoiced")}</span>
                    <strong>
                      {formatMoney(loanCase.financialSummary?.totalInvoiced)}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t("workspace.cases.remaining", "Lender remaining")}
                    </span>
                    <strong>
                      {formatMoney(loanCase.financialSummary?.remainingAmount)}
                    </strong>
                  </div>
                </div>

                {loanCase.lastUpdate ? (
                  <small>
                    {t("workspace.cases.updated", "Updated")}{" "}
                    {formatDate(loanCase.lastUpdate)}
                  </small>
                ) : null}
              </button>
            );
          })
        )}
      </div>
    </section>
  );
}

/* =========================================================
   FINANCIAL SUMMARY
   ========================================================= */

function FinancialSummary({ loanCase, t }) {
  if (!loanCase) return null;

  const {
    renovationFinancing,
    totalInvoiced,
    totalFundingAllocated,
    invoiceUnallocatedAmount,
    lenderLoanAllocated,
    lenderLoanRemaining,
    financingLabel,
  } = loanCase.financialSummary;

  const fundingSources = Array.isArray(loanCase.fundingSources)
    ? loanCase.fundingSources
    : [];

  const visibleFundingSources = fundingSources.filter(
    (source) => getFundingSourceTotal(source) > 0,
  );

  const activeFundingSources = visibleFundingSources.filter(
    (source) => !isUnavailableFundingStatus(source.status),
  );

  const potentialFunding = activeFundingSources.reduce(
    (total, source) => total + getFundingSourceTotal(source),
    0,
  );

  const confirmedFunding = activeFundingSources
    .filter((source) => isConfirmedFundingStatus(source.status))
    .reduce((total, source) => total + getFundingSourceTotal(source), 0);

  const expectedFunding = activeFundingSources
    .filter((source) => isProvisionalFundingStatus(source.status))
    .reduce((total, source) => total + getFundingSourceTotal(source), 0);

  const invoiced = Number(totalInvoiced || 0);
  const allocated = Number(totalFundingAllocated || 0);

  const progressTarget =
    potentialFunding > 0 ? potentialFunding : Number(renovationFinancing || 0);

  const rawInvoicedCoveragePercentage =
    progressTarget > 0 ? (invoiced / progressTarget) * 100 : 0;

  const visualInvoicedCoveragePercentage = Math.min(
    Math.max(rawInvoicedCoveragePercentage, 0),
    100,
  );

  const remainingPotentialToInvoice =
    progressTarget > 0 ? Math.max(progressTarget - invoiced, 0) : 0;

  const headingCode =
    loanCase.project?.code ||
    loanCase.caseReference ||
    `#${loanCase.loanCaseId}`;

  return (
    <section className="lender-invoices-financial">
      <div className="lender-invoices-financial__heading">
        <div>
          <span>
            {loanCase.project
              ? t("workspace.financial.projectKicker", "Project financing")
              : t("workspace.financial.caseKicker", "Renovation financing")}
          </span>

          <h2>
            {headingCode}
            {loanCase.homeowner ? ` · ${loanCase.homeowner}` : ""}
          </h2>

          <small>
            {loanCase.project?.code
              ? t(
                  "workspace.financial.projectTracking",
                  "Track renovation invoices across the lender loan and the other project funding sources. Expected sources can be used for provisional invoice allocations until their status is confirmed.",
                )
              : t(
                  "workspace.financial.caseTracking",
                  "Invoices are visible for this loan case. Funding allocations require a linked renovation project.",
                )}
          </small>
        </div>

        {loanCase.status ? (
          <span className="lender-invoices-case-status">
            {formatStatus(loanCase.status)}
          </span>
        ) : null}
      </div>

      <div className="lender-invoices-financial__cards">
        <div className="lender-invoices-financial-card">
          <span className="lender-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>
          <div>
            <small>
              {financingLabel ||
                t(
                  "workspace.financial.financing",
                  "Lender renovation financing",
                )}
            </small>
            <strong>{formatMoney(renovationFinancing)}</strong>
          </div>
        </div>

        <div className="lender-invoices-financial-card lender-invoices-financial-card--expected">
          <span className="lender-invoices-financial-card__icon">
            <Landmark size={18} />
          </span>
          <div>
            <small>
              {t("workspace.financial.expectedFunding", "Expected funding")}
            </small>
            <strong>{formatMoney(expectedFunding)}</strong>
          </div>
        </div>

        <div className="lender-invoices-financial-card lender-invoices-financial-card--potential">
          <span className="lender-invoices-financial-card__icon">
            <WalletCards size={18} />
          </span>
          <div>
            <small>
              {t(
                "workspace.financial.potentialFunding",
                "Potential renovation funding",
              )}
            </small>
            <strong>{formatMoney(potentialFunding)}</strong>
          </div>
        </div>

        <div className="lender-invoices-financial-card">
          <span className="lender-invoices-financial-card__icon">
            <ReceiptText size={18} />
          </span>
          <div>
            <small>{t("workspace.financial.invoiced", "Total invoiced")}</small>
            <strong>{formatMoney(totalInvoiced)}</strong>
          </div>
        </div>

        <div className="lender-invoices-financial-card">
          <span className="lender-invoices-financial-card__icon">
            <CheckCircle2 size={18} />
          </span>
          <div>
            <small>
              {t(
                "workspace.financial.allSourcesAllocated",
                "Invoice value allocated",
              )}
            </small>
            <strong>{formatMoney(totalFundingAllocated)}</strong>
          </div>
        </div>

        <div className="lender-invoices-financial-card lender-invoices-financial-card--unallocated">
          <span className="lender-invoices-financial-card__icon">
            <FileText size={18} />
          </span>
          <div>
            <small>
              {t(
                "workspace.financial.unallocated",
                "Invoice value unallocated",
              )}
            </small>
            <strong>{formatMoney(invoiceUnallocatedAmount)}</strong>
          </div>
        </div>
      </div>

      {visibleFundingSources.length ? (
        <div className="lender-invoices-funding-sources-overview">
          <div className="lender-invoices-funding-sources-overview__header">
            <div>
              <strong>{t("workspace.funding.title", "Funding sources")}</strong>
              <span>
                {t(
                  "workspace.funding.subtitle",
                  "Invoice allocations may be split across all eligible sources. Expected sources are shown as provisional.",
                )}
              </span>
            </div>

            <div className="lender-invoices-funding-sources-overview__totals">
              <span>
                {t("workspace.funding.confirmed", "Confirmed")}
                <strong>{formatMoney(confirmedFunding)}</strong>
              </span>
              <span>
                {t("workspace.funding.expected", "Expected")}
                <strong>{formatMoney(expectedFunding)}</strong>
              </span>
            </div>
          </div>

          <div className="lender-invoices-funding-source-grid">
            {visibleFundingSources.map((source) => (
              <div
                key={source.id}
                className={`lender-invoices-funding-source-card ${
                  source.isCurrentLenderLoan ? "is-lender" : ""
                } ${
                  isProvisionalFundingStatus(source.status)
                    ? "is-provisional"
                    : ""
                }`}
              >
                <div className="lender-invoices-funding-source-card__top">
                  <div>
                    <strong>{getFundingSourceName(source, t)}</strong>
                    {source.providerName ? (
                      <span>{source.providerName}</span>
                    ) : null}
                  </div>

                  <span
                    className={`lender-invoices-funding-status ${getFundingStatusClass(
                      source,
                    )}`}
                  >
                    {getFundingStatusLabel(source, t)}
                  </span>
                </div>

                <div className="lender-invoices-funding-source-card__amounts">
                  <div>
                    <span>{t("workspace.funding.total", "Total")}</span>
                    <strong>
                      {formatMoney(getFundingSourceTotal(source))}
                    </strong>
                  </div>
                  <div>
                    <span>{t("workspace.funding.allocated", "Allocated")}</span>
                    <strong>{formatMoney(source.allocatedAmount)}</strong>
                  </div>
                  <div>
                    <span>{t("workspace.funding.remaining", "Remaining")}</span>
                    <strong>{formatMoney(source.remainingAmount)}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {progressTarget > 0 ? (
        <div className="lender-invoices-budget-progress lender-invoices-budget-progress--coverage">
          <div className="lender-invoices-budget-progress__top">
            <div>
              <span>
                {t(
                  "workspace.financial.progress",
                  "Renovation invoiced against potential funding",
                )}
              </span>
              <small>
                {t(
                  "workspace.financial.progressHint",
                  "Shows uploaded invoice value against the current potential renovation funding envelope.",
                )}
              </small>
            </div>

            <strong>{rawInvoicedCoveragePercentage.toFixed(1)}%</strong>
          </div>

          <div className="lender-invoices-budget-progress__track">
            <span style={{ width: `${visualInvoicedCoveragePercentage}%` }} />
          </div>

          <div className="lender-invoices-budget-progress__legend">
            <span>
              {t("workspace.financial.invoiced", "Invoiced")}:{" "}
              <strong>{formatMoney(totalInvoiced)}</strong>
            </span>
            <span>
              {t("workspace.financial.potentialFunding", "Potential funding")}:{" "}
              <strong>{formatMoney(progressTarget)}</strong>
            </span>
            <span>
              {t(
                "workspace.financial.remainingPotentialToInvoice",
                "Remaining to invoice",
              )}
              : <strong>{formatMoney(remainingPotentialToInvoice)}</strong>
            </span>
          </div>

          <div className="lender-invoices-budget-progress__allocation-note">
            <span>
              {t(
                "workspace.financial.allSourcesAllocated",
                "Invoice value allocated",
              )}
              : <strong>{formatMoney(allocated)}</strong>
            </span>
            <span>
              {t("workspace.financial.unallocatedShort", "Unallocated")}:{" "}
              <strong>{formatMoney(invoiceUnallocatedAmount)}</strong>
            </span>
          </div>
        </div>
      ) : null}

      {Number(lenderLoanAllocated || 0) > 0 ||
      Number(lenderLoanRemaining || 0) > 0 ? (
        <div className="lender-invoices-lender-balance-note">
          <span>
            {t("workspace.financial.lenderAllocated", "Allocated from lender")}:{" "}
            <strong>{formatMoney(lenderLoanAllocated)}</strong>
          </span>
          <span>
            {t(
              "workspace.financial.lenderRemaining",
              "Lender financing remaining",
            )}
            : <strong>{formatMoney(lenderLoanRemaining)}</strong>
          </span>
        </div>
      ) : null}
    </section>
  );
}

/* =========================================================
   INVOICE LIST
   ========================================================= */

function InvoiceList({ invoices, selectedInvoiceId, onSelect, loading, t }) {
  const [search, setSearch] = React.useState("");

  const filteredInvoices = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return invoices;

    return invoices.filter((invoice) =>
      [
        invoice.filename,
        invoice.renovationTypeLabel,
        invoice.amount,
        `v${invoice.latestVersionNumber}`,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [invoices, search]);

  return (
    <section className="lender-invoices-list-panel">
      <div className="lender-invoices-panel-header">
        <div>
          <span className="lender-invoices-panel-kicker">
            <ReceiptText size={14} />
            {t("workspace.invoices.kicker", "Invoices")}
          </span>
          <h2>{t("workspace.invoices.title", "Renovation invoices")}</h2>
        </div>

        <span className="lender-invoices-count-pill">{invoices.length}</span>
      </div>

      <label className="lender-invoices-search">
        <Search size={16} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t(
            "workspace.invoices.search",
            "Search invoice, type or amount...",
          )}
          disabled={loading}
        />
      </label>

      <div className="lender-invoices-list-content">
        {filteredInvoices.length === 0 ? (
          <div className="lender-invoices-state lender-invoices-state--large">
            <ReceiptText size={24} />
            <strong>
              {t("workspace.invoices.emptyTitle", "No invoices found")}
            </strong>
            <span>
              {t(
                "workspace.invoices.emptyText",
                "Invoices uploaded for this renovation will appear here.",
              )}
            </span>
          </div>
        ) : (
          <div className="lender-invoices-table">
            <div className="lender-invoices-table__head">
              <span>{t("workspace.invoices.columns.file", "File")}</span>
              <span>{t("workspace.invoices.columns.type", "Renovation")}</span>
              <span>{t("workspace.invoices.columns.amount", "Amount")}</span>
              <span>{t("workspace.invoices.columns.version", "Version")}</span>
            </div>

            <div className="lender-invoices-table__body">
              {filteredInvoices.map((invoice) => {
                const selected =
                  String(invoice.id) === String(selectedInvoiceId);

                return (
                  <button
                    key={invoice.id}
                    type="button"
                    className={`lender-invoices-table-row ${
                      selected ? "is-selected" : ""
                    }`}
                    onClick={() => onSelect(invoice.id)}
                    disabled={loading}
                  >
                    <div className="lender-invoices-table-row__file">
                      <span className="lender-invoices-mobile-label">
                        {t("workspace.invoices.columns.file", "File")}
                      </span>
                      <strong>
                        {invoice.filename ||
                          t("workspace.invoices.defaultFilename", "Invoice")}
                      </strong>
                      <small>{formatDateTime(invoice.uploadedAt)}</small>
                    </div>

                    <div>
                      <span className="lender-invoices-mobile-label">
                        {t("workspace.invoices.columns.type", "Renovation")}
                      </span>
                      <span>{invoice.renovationTypeLabel}</span>
                    </div>

                    <div className="lender-invoices-table-row__amount">
                      <span className="lender-invoices-mobile-label">
                        {t("workspace.invoices.columns.amount", "Amount")}
                      </span>

                      <strong>{formatMoney(invoice.amount)}</strong>

                      <small>
                        {t("workspace.invoices.allocated", "Allocated")}{" "}
                        {formatMoney(invoice.allocatedAmount)}
                      </small>

                      {Number(invoice.unallocatedAmount || 0) > 0 ? (
                        <small className="is-unallocated">
                          {formatMoney(invoice.unallocatedAmount)}{" "}
                          {t("workspace.invoices.unallocated", "unallocated")}
                        </small>
                      ) : null}
                    </div>

                    <div>
                      <span className="lender-invoices-mobile-label">
                        {t("workspace.invoices.columns.version", "Version")}
                      </span>

                      <div className="lender-invoices-version-cell">
                        <span>v{invoice.latestVersionNumber}</span>

                        {invoice.versionCount > 1 ? (
                          <span className="lender-invoices-updated-badge">
                            {t("workspace.invoices.updated", "Updated")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="lender-invoices-content-loader">
            <div className="lender-invoices-content-loader__inner">
              <LoaderCircle size={27} className="lender-invoices-spin" />
              <strong>
                {t(
                  "workspace.caseLoading.title",
                  "Loading renovation invoices",
                )}
              </strong>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* =========================================================
   VERSION HISTORY
   ========================================================= */

function VersionHistory({ invoice, onViewVersion, onDownloadVersion, t }) {
  if (!invoice?.versions?.length) return null;

  return (
    <div className="lender-invoice-details__versions">
      {invoice.versions.map((version) => {
        const isLatest = version.versionNumber === invoice.latestVersionNumber;

        return (
          <div
            key={version.id ?? `${invoice.id}-${version.versionNumber}`}
            className={`lender-invoice-version ${isLatest ? "is-latest" : ""}`}
          >
            <div className="lender-invoice-version__top">
              <div>
                <strong>v{version.versionNumber}</strong>

                {isLatest ? (
                  <span>{t("workspace.details.latest", "Latest")}</span>
                ) : null}
              </div>

              <small>{formatDateTime(version.uploadedAt)}</small>
            </div>

            <p>{version.filename || "—"}</p>

            <div className="lender-invoice-version__meta">
              <strong>{formatMoney(version.amount)}</strong>
              {version.uploadedByName ? (
                <span>{version.uploadedByName}</span>
              ) : null}
            </div>

            {version.notes ? (
              <small className="lender-invoice-version__note">
                {version.notes}
              </small>
            ) : null}

            <div className="lender-invoice-version__actions">
              <button
                type="button"
                onClick={() => onViewVersion(version, invoice)}
              >
                <Eye size={14} />
                {t("workspace.actions.view", "View")}
              </button>

              <button
                type="button"
                onClick={() => onDownloadVersion(version, invoice)}
              >
                <Download size={14} />
                {t("workspace.actions.download", "Download")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================
   FUNDING ALLOCATION MODAL
   ========================================================= */

function FundingAllocationModal({
  open,
  mode,
  invoice,
  fundingSources,
  allocation,
  busy,
  onClose,
  onSubmit,
  t,
}) {
  const [selectedFundingSourceId, setSelectedFundingSourceId] =
    React.useState("");
  const [amount, setAmount] = React.useState("");
  const [eligibilityConfirmed, setEligibilityConfirmed] = React.useState(false);
  const [eligibilityNotes, setEligibilityNotes] = React.useState("");
  const [evidenceInvoiceVersionId, setEvidenceInvoiceVersionId] =
    React.useState("");
  const [submitError, setSubmitError] = React.useState(null);

  const allSources = Array.isArray(fundingSources) ? fundingSources : [];

  const selectableSources = React.useMemo(() => {
    return allSources.filter((source) => {
      if (!canLenderAllocateFromSource(source)) return false;

      const isCurrentAllocationSource =
        allocation && String(allocation.fundingSourceId) === String(source.id);

      return (
        isCurrentAllocationSource || Number(source.remainingAmount || 0) > 0
      );
    });
  }, [allSources, allocation]);

  React.useEffect(() => {
    if (!open) return;

    const initialSourceId =
      allocation?.fundingSourceId ?? selectableSources[0]?.id ?? "";

    setSelectedFundingSourceId(initialSourceId ? String(initialSourceId) : "");
    setAmount(allocation?.amount ?? "");
    setEligibilityConfirmed(Boolean(allocation?.eligibilityConfirmed));
    setEligibilityNotes(allocation?.eligibilityNotes ?? "");
    setEvidenceInvoiceVersionId(
      allocation?.evidenceInvoiceVersionId ?? invoice?.latestVersion?.id ?? "",
    );
    setSubmitError(null);
  }, [open, allocation, invoice, selectableSources]);

  React.useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, busy, onClose]);

  if (!open || !invoice) return null;

  const fundingSource = allSources.find(
    (source) => String(source.id) === String(selectedFundingSourceId),
  );

  const currentAllocationAmount = Number(allocation?.amount || 0);
  const invoiceUnallocated = Number(invoice.unallocatedAmount || 0);
  const sourceRemaining = Number(fundingSource?.remainingAmount || 0);

  const sameSourceAsExistingAllocation = Boolean(
    mode === "edit" &&
    allocation?.fundingSourceId &&
    fundingSource?.id &&
    String(allocation.fundingSourceId) === String(fundingSource.id),
  );

  const invoiceAvailableForEdit =
    invoiceUnallocated + (mode === "edit" ? currentAllocationAmount : 0);

  const sourceAvailableForEdit =
    sourceRemaining +
    (sameSourceAsExistingAllocation ? currentAllocationAmount : 0);

  const maximumAmount = fundingSource
    ? Math.max(0, Math.min(invoiceAvailableForEdit, sourceAvailableForEdit))
    : 0;

  const numericAmount = Number(amount);
  const amountIsValid =
    Boolean(fundingSource) &&
    amount !== "" &&
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    numericAmount <= maximumAmount;

  const sourceIsProvisional = isProvisionalFundingStatus(fundingSource?.status);

  async function handleSubmit(event) {
    event.preventDefault();

    if (!amountIsValid || busy || !fundingSource) return;

    setSubmitError(null);

    const payload = {
      funding_source_id: fundingSource.id,
      amount: String(amount),
      eligibility_confirmed: eligibilityConfirmed,
      eligibility_notes: eligibilityNotes.trim(),
      evidence_invoice_version_id: evidenceInvoiceVersionId
        ? Number(evidenceInvoiceVersionId)
        : null,
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      const details = Array.isArray(error?.details) ? error.details : [];

      setSubmitError({
        title:
          error?.title ||
          t(
            "workspace.allocation.errors.save",
            "The funding allocation could not be saved.",
          ),
        details:
          details.length > 0
            ? details
            : [
                {
                  field: "",
                  message:
                    error?.message ||
                    t(
                      "workspace.allocation.errors.save",
                      "The funding allocation could not be saved.",
                    ),
                },
              ],
      });
    }
  }

  return (
    <div
      className="lender-allocation-modal"
      onClick={busy ? undefined : onClose}
    >
      <form
        className="lender-allocation-modal__dialog"
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lender-allocation-modal__header">
          <div>
            <span>
              {mode === "edit"
                ? t("workspace.allocation.editKicker", "Edit allocation")
                : t("workspace.allocation.createKicker", "Allocate invoice")}
            </span>

            <h3>
              {invoice.filename ||
                t("workspace.invoices.defaultFilename", "Invoice")}
            </h3>
          </div>

          <button type="button" onClick={onClose} disabled={busy}>
            <X size={18} />
          </button>
        </div>

        <div className="lender-allocation-modal__body">
          <div className="lender-allocation-edit-grid">
            <label className="lender-allocation-field lender-allocation-field--source">
              <span>{t("workspace.allocation.source", "Funding source")}</span>

              <select
                value={selectedFundingSourceId}
                onChange={(event) => {
                  setSelectedFundingSourceId(event.target.value);
                  setSubmitError(null);
                }}
                disabled={busy || selectableSources.length === 0}
                required
              >
                <option value="">
                  {t(
                    "workspace.allocation.selectSource",
                    "Select a funding source",
                  )}
                </option>

                {selectableSources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {getFundingSourceName(source, t)} ·{" "}
                    {getFundingStatusLabel(source, t)} ·{" "}
                    {formatMoney(source.remainingAmount)}{" "}
                    {t("workspace.allocation.availableLower", "available")}
                  </option>
                ))}
              </select>

              {mode === "edit" ? (
                <small className="lender-allocation-field__help">
                  {t(
                    "workspace.allocation.changeSourceHint",
                    "You can move this allocation to another eligible funding source.",
                  )}
                </small>
              ) : null}
            </label>

            <label className="lender-allocation-field lender-allocation-field--amount">
              <span>
                {t("workspace.allocation.amount", "Allocation amount")}
              </span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={maximumAmount || undefined}
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setSubmitError(null);
                }}
                placeholder="0.00"
                disabled={busy}
                required
              />

              {amount && !amountIsValid ? (
                <small className="lender-allocation-field__error">
                  {t(
                    "workspace.allocation.invalidAmount",
                    "Enter an amount greater than zero and no higher than the available maximum.",
                  )}
                </small>
              ) : null}
            </label>
          </div>

          {fundingSource ? (
            <>
              <div
                className={`lender-allocation-source ${
                  sourceIsProvisional ? "is-provisional" : ""
                }`}
              >
                <div>
                  <span>
                    {t(
                      "workspace.allocation.selectedSource",
                      "Selected source",
                    )}
                  </span>
                  <strong>{getFundingSourceName(fundingSource, t)}</strong>
                  {fundingSource.providerName ? (
                    <small>{fundingSource.providerName}</small>
                  ) : null}
                </div>

                <div className="lender-allocation-source__status-block">
                  <span
                    className={`lender-invoices-funding-status ${getFundingStatusClass(
                      fundingSource,
                    )}`}
                  >
                    {getFundingStatusLabel(fundingSource, t)}
                  </span>
                  <strong>{formatMoney(sourceAvailableForEdit)}</strong>
                  <small>
                    {t("workspace.allocation.available", "Available")}
                  </small>
                </div>
              </div>

              {sourceIsProvisional ? (
                <div className="lender-allocation-provisional-note">
                  <Info size={17} />
                  <div>
                    <strong>
                      {t(
                        "workspace.allocation.provisionalTitle",
                        "Provisional allocation",
                      )}
                    </strong>
                    <span>
                      {t(
                        "workspace.allocation.provisionalText",
                        "This funding source is still expected or pending. The invoice can be earmarked to it now, but the allocation remains dependent on the source being confirmed.",
                      )}
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="lender-allocation-limits">
                <div>
                  <span>
                    {t("workspace.allocation.invoiceAmount", "Invoice")}
                  </span>
                  <strong>{formatMoney(invoice.amount)}</strong>
                </div>

                <div>
                  <span>
                    {t(
                      "workspace.allocation.invoiceAvailable",
                      "Available on invoice",
                    )}
                  </span>
                  <strong>{formatMoney(invoiceAvailableForEdit)}</strong>
                </div>

                <div>
                  <span>{t("workspace.allocation.maximum", "Maximum")}</span>
                  <strong>{formatMoney(maximumAmount)}</strong>
                </div>
              </div>

              <label className="lender-allocation-field">
                <span>
                  {t(
                    "workspace.allocation.evidenceVersion",
                    "Evidence version",
                  )}
                </span>
                <select
                  value={evidenceInvoiceVersionId}
                  onChange={(event) => {
                    setEvidenceInvoiceVersionId(event.target.value);
                    setSubmitError(null);
                  }}
                  disabled={busy}
                >
                  <option value="">
                    {t(
                      "workspace.allocation.noEvidence",
                      "No evidence version",
                    )}
                  </option>

                  {invoice.versions.map((version) => (
                    <option key={version.id} value={version.id}>
                      v{version.versionNumber} · {version.filename || "Invoice"}
                    </option>
                  ))}
                </select>
              </label>

              <label className="lender-allocation-check">
                <input
                  type="checkbox"
                  checked={eligibilityConfirmed}
                  onChange={(event) => {
                    setEligibilityConfirmed(event.target.checked);
                    setSubmitError(null);
                  }}
                  disabled={busy}
                />

                <span>
                  <CheckCircle2 size={17} />
                  {t(
                    "workspace.allocation.eligibilityConfirmed",
                    "Eligibility for this allocation has been checked",
                  )}
                </span>
              </label>

              <label className="lender-allocation-field">
                <span>
                  {t(
                    "workspace.allocation.eligibilityNotes",
                    "Eligibility notes",
                  )}
                </span>
                <textarea
                  rows={4}
                  value={eligibilityNotes}
                  onChange={(event) => {
                    setEligibilityNotes(event.target.value);
                    setSubmitError(null);
                  }}
                  disabled={busy}
                  placeholder={t(
                    "workspace.allocation.notesPlaceholder",
                    "Optional notes about the allocation or expected funding conditions...",
                  )}
                />
              </label>
            </>
          ) : (
            <div className="lender-allocation-modal__empty-source">
              <WalletCards size={24} />
              <strong>
                {t(
                  "workspace.allocation.noSourcesTitle",
                  "No funding source available",
                )}
              </strong>
              <span>
                {t(
                  "workspace.allocation.noSourcesText",
                  "This project does not currently have an invoice-fundable source with available balance.",
                )}
              </span>
            </div>
          )}

          {submitError ? (
            <div className="lender-allocation-modal__error" role="alert">
              <AlertTriangle size={18} />
              <div>
                <strong>{submitError.title}</strong>
                <ul>
                  {submitError.details.map((detail, index) => (
                    <li key={`${detail.field}-${detail.message}-${index}`}>
                      {detail.field ? <b>{detail.field}: </b> : null}
                      {detail.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>

        <div className="lender-allocation-modal__footer">
          <button
            type="button"
            className="secondary"
            onClick={onClose}
            disabled={busy}
          >
            {t("workspace.allocation.cancel", "Cancel")}
          </button>

          <button
            type="submit"
            className="primary"
            disabled={!amountIsValid || busy || !fundingSource}
          >
            {busy ? (
              <LoaderCircle size={16} className="lender-invoices-spin" />
            ) : mode === "edit" ? (
              <Pencil size={15} />
            ) : (
              <Plus size={15} />
            )}

            {mode === "edit"
              ? t("workspace.allocation.saveChanges", "Save changes")
              : t("workspace.allocation.create", "Create allocation")}
          </button>
        </div>
      </form>
    </div>
  );
}

/* =========================================================
   FUNDING ALLOCATIONS
   ========================================================= */

function FundingAllocations({
  invoice,
  fundingSources,
  busyAllocationId,
  onCreate,
  onEdit,
  onDelete,
  t,
}) {
  const allocations = invoice?.fundingAllocations ?? [];
  const allSources = Array.isArray(fundingSources) ? fundingSources : [];

  const selectableSources = allSources.filter(
    (source) =>
      canLenderAllocateFromSource(source) &&
      Number(source.remainingAmount || 0) > 0,
  );

  const canCreate = Boolean(
    Number(invoice?.unallocatedAmount || 0) > 0 && selectableSources.length > 0,
  );

  return (
    <div className="lender-invoice-details__funding">
      <div className="lender-invoice-details__section-title lender-invoice-details__section-title--actions">
        <div>
          <WalletCards size={15} />
          <h4>{t("workspace.details.funding", "Funding allocations")}</h4>
        </div>

        <button
          type="button"
          className="lender-invoice-allocation-add"
          onClick={() => onCreate(invoice)}
          disabled={!canCreate}
          title={
            Number(invoice?.unallocatedAmount || 0) <= 0
              ? t(
                  "workspace.details.invoiceFullyAllocated",
                  "This invoice is already fully allocated.",
                )
              : selectableSources.length === 0
                ? t(
                    "workspace.details.noAllocatableSources",
                    "No invoice-fundable source currently has available balance.",
                  )
                : undefined
          }
        >
          <Plus size={14} />
          {t("workspace.details.allocate", "Allocate")}
        </button>
      </div>

      {allSources.length ? (
        <div className="lender-invoice-source-mini-list">
          {allSources
            .filter((source) => source.canFundInvoices)
            .map((source) => (
              <div
                key={source.id}
                className={`lender-invoice-source-mini ${
                  source.isCurrentLenderLoan ? "is-lender" : ""
                } ${isProvisionalFundingStatus(source.status) ? "is-provisional" : ""}`}
              >
                <div>
                  <strong>{getFundingSourceName(source, t)}</strong>
                  <span
                    className={`lender-invoices-funding-status ${getFundingStatusClass(
                      source,
                    )}`}
                  >
                    {getFundingStatusLabel(source, t)}
                  </span>
                </div>

                <span>
                  {formatMoney(source.remainingAmount)}{" "}
                  {t("workspace.details.available", "available")}
                </span>
              </div>
            ))}
        </div>
      ) : (
        <div className="lender-invoice-details__no-allocation">
          {t(
            "workspace.details.noFundingSources",
            "No project funding sources are available yet.",
          )}
        </div>
      )}

      {allocations.length ? (
        <div className="lender-invoice-allocations">
          {allocations.map((item) => {
            const source = allSources.find(
              (candidate) =>
                String(candidate.id) === String(item.fundingSourceId),
            );

            const delegated = Boolean(
              item.actingRole &&
              item.operatorRole &&
              item.actingRole !== item.operatorRole,
            );

            const isBusy = String(busyAllocationId) === String(item.id);
            const provisional = isProvisionalFundingStatus(source?.status);
            const canManage = Boolean(source?.id && source?.canFundInvoices);

            return (
              <div
                key={item.id}
                className={`lender-invoice-allocation ${
                  item.isCurrentLenderLoan ? "is-lender" : ""
                } ${provisional ? "is-provisional" : ""}`}
              >
                <div className="lender-invoice-allocation__top">
                  <div>
                    <strong>
                      {source
                        ? getFundingSourceName(source, t)
                        : item.financingProduct ||
                          item.fundingTypeLabel ||
                          t(
                            "workspace.details.fundingSource",
                            "Funding source",
                          )}
                    </strong>

                    {source?.providerName || item.providerName ? (
                      <span>{source?.providerName || item.providerName}</span>
                    ) : null}
                  </div>

                  <strong>{formatMoney(item.amount)}</strong>
                </div>

                <div className="lender-invoice-allocation__meta">
                  {source ? (
                    <span
                      className={`lender-invoices-funding-status ${getFundingStatusClass(
                        source,
                      )}`}
                    >
                      {getFundingStatusLabel(source, t)}
                    </span>
                  ) : null}

                  {item.isCurrentLenderLoan ? (
                    <span className="is-lender-source">
                      {t("workspace.details.yourLoan", "Your lender financing")}
                    </span>
                  ) : null}

                  {item.eligibilityConfirmed ? (
                    <span className="is-confirmed">
                      <CheckCircle2 size={12} />
                      {t("workspace.details.eligible", "Eligibility checked")}
                    </span>
                  ) : null}
                </div>

                {provisional ? (
                  <small className="lender-invoice-allocation__provisional-note">
                    {t(
                      "workspace.details.provisionalAllocation",
                      "Provisional allocation: this source is not yet fully confirmed.",
                    )}
                  </small>
                ) : null}

                {delegated ? (
                  <small className="lender-invoice-allocation__acting">
                    {t("workspace.details.enteredBy", "Entered by")}{" "}
                    {item.operatorRole}
                    {" · "}
                    {t("workspace.details.onBehalfOf", "on behalf of")}{" "}
                    {item.actingRole}
                    {item.actingOrganisationName
                      ? ` · ${item.actingOrganisationName}`
                      : ""}
                  </small>
                ) : null}

                {item.eligibilityNotes ? (
                  <small className="lender-invoice-allocation__notes">
                    {item.eligibilityNotes}
                  </small>
                ) : null}

                {canManage ? (
                  <div className="lender-invoice-allocation__actions">
                    <button
                      type="button"
                      onClick={() => onEdit(item, invoice)}
                      disabled={
                        isBusy || isUnavailableFundingStatus(source?.status)
                      }
                    >
                      <Pencil size={13} />
                      {t("workspace.actions.edit", "Edit")}
                    </button>

                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => onDelete(item, invoice)}
                      disabled={isBusy}
                    >
                      {isBusy ? (
                        <LoaderCircle
                          size={13}
                          className="lender-invoices-spin"
                        />
                      ) : (
                        <Trash2 size={13} />
                      )}
                      {t("workspace.actions.delete", "Delete")}
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="lender-invoice-details__no-allocation">
          {t(
            "workspace.details.noAllocations",
            "No funding has been allocated to this invoice yet.",
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   INVOICE DETAILS
   ========================================================= */

function InvoiceDetails({
  invoice,
  fundingSources,
  loading,
  busyAllocationId,
  onViewVersion,
  onDownloadVersion,
  onCreateAllocation,
  onEditAllocation,
  onDeleteAllocation,
  t,
}) {
  if (!invoice) {
    return (
      <aside className="lender-invoice-details lender-invoice-details--empty">
        <FileText size={28} />
        <h3>{t("workspace.details.emptyTitle", "Select an invoice")}</h3>
        <p>
          {t(
            "workspace.details.emptyText",
            "Choose an invoice to view its details, funding allocations and version history.",
          )}
        </p>

        {loading ? (
          <div className="lender-invoices-content-loader">
            <div className="lender-invoices-content-loader__inner">
              <LoaderCircle size={27} className="lender-invoices-spin" />
              <strong>
                {t("workspace.details.loading", "Loading invoice details")}
              </strong>
            </div>
          </div>
        ) : null}
      </aside>
    );
  }

  return (
    <aside className="lender-invoice-details">
      <div className="lender-invoice-details__header">
        <div className="lender-invoice-details__file-icon">
          <FileText size={20} />
        </div>

        <div>
          <span>{t("workspace.details.kicker", "Invoice details")}</span>
          <h3>
            {invoice.filename ||
              t("workspace.invoices.defaultFilename", "Invoice")}
          </h3>
          <p>{invoice.renovationTypeLabel}</p>
        </div>
      </div>

      <div className="lender-invoice-details__amount">
        <span>{t("workspace.details.amount", "Current amount")}</span>
        <strong>{formatMoney(invoice.amount)}</strong>
      </div>

      <div className="lender-invoice-details__allocation-summary">
        <div>
          <span>{t("workspace.details.allocated", "Funding allocated")}</span>
          <strong>{formatMoney(invoice.allocatedAmount)}</strong>
        </div>

        <div>
          <span>{t("workspace.details.unallocated", "Still unallocated")}</span>
          <strong>{formatMoney(invoice.unallocatedAmount)}</strong>
        </div>
      </div>

      <div className="lender-invoice-details__facts">
        <div>
          <span>{t("workspace.details.version", "Current version")}</span>
          <strong>v{invoice.latestVersionNumber}</strong>
        </div>

        <div>
          <span>{t("workspace.details.uploaded", "Uploaded")}</span>
          <strong>{formatDateTime(invoice.uploadedAt)}</strong>
        </div>

        <div>
          <span>{t("workspace.details.versions", "Versions")}</span>
          <strong>{invoice.versionCount}</strong>
        </div>
      </div>

      {invoice.notes ? (
        <div className="lender-invoice-details__note">
          <span>{t("workspace.details.note", "Homebuyer note")}</span>
          <p>{invoice.notes}</p>
        </div>
      ) : null}

      {invoice.latestVersion ? (
        <div className="lender-invoice-details__primary-actions">
          <button
            type="button"
            className="primary"
            onClick={() => onViewVersion(invoice.latestVersion, invoice)}
          >
            <Eye size={15} />
            {t("workspace.actions.viewInvoice", "View invoice")}
          </button>

          <button
            type="button"
            className="secondary"
            onClick={() => onDownloadVersion(invoice.latestVersion, invoice)}
          >
            <Download size={15} />
            {t("workspace.actions.download", "Download")}
          </button>
        </div>
      ) : null}

      <FundingAllocations
        invoice={invoice}
        fundingSources={fundingSources}
        busyAllocationId={busyAllocationId}
        onCreate={onCreateAllocation}
        onEdit={onEditAllocation}
        onDelete={onDeleteAllocation}
        t={t}
      />

      <div className="lender-invoice-details__history">
        <div className="lender-invoice-details__section-title">
          <History size={15} />
          <h4>{t("workspace.details.history", "Version history")}</h4>
        </div>

        <VersionHistory
          invoice={invoice}
          onViewVersion={onViewVersion}
          onDownloadVersion={onDownloadVersion}
          t={t}
        />
      </div>
    </aside>
  );
}

/* =========================================================
   DELETE ALLOCATION MODAL
   ========================================================= */

function DeleteAllocationModal({
  open,
  allocation,
  invoice,
  fundingSources,
  busy,
  error,
  onClose,
  onConfirm,
  t,
}) {
  React.useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, busy, onClose]);

  if (!open || !allocation) return null;

  const source = (Array.isArray(fundingSources) ? fundingSources : []).find(
    (candidate) => String(candidate.id) === String(allocation.fundingSourceId),
  );

  const sourceName = source
    ? getFundingSourceName(source, t)
    : allocation.financingProduct ||
      allocation.fundingTypeLabel ||
      t("workspace.details.fundingSource", "Funding source");

  return (
    <div className="lender-delete-modal" onClick={busy ? undefined : onClose}>
      <div
        className="lender-delete-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lender-delete-allocation-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lender-delete-modal__icon">
          <Trash2 size={24} />
        </div>

        <div className="lender-delete-modal__content">
          <span className="lender-delete-modal__eyebrow">
            {t("workspace.allocation.deleteKicker", "Remove allocation")}
          </span>

          <h3 id="lender-delete-allocation-title">
            {t(
              "workspace.allocation.deleteTitle",
              "Delete this funding allocation?",
            )}
          </h3>

          <p>
            {t(
              "workspace.allocation.deleteText",
              "The amount will be released back to the funding source and the invoice will become unallocated by the same amount.",
            )}
          </p>

          <div className="lender-delete-modal__summary">
            <div>
              <span>{t("workspace.allocation.invoice", "Invoice")}</span>
              <strong>{invoice?.filename || "—"}</strong>
            </div>
            <div>
              <span>{t("workspace.allocation.source", "Funding source")}</span>
              <strong>{sourceName}</strong>
            </div>
            <div>
              <span>
                {t("workspace.allocation.amount", "Allocation amount")}
              </span>
              <strong>{formatMoney(allocation.amount)}</strong>
            </div>
          </div>

          {error ? (
            <div className="lender-delete-modal__error" role="alert">
              <AlertTriangle size={17} />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        <div className="lender-delete-modal__actions">
          <button
            type="button"
            className="secondary"
            onClick={onClose}
            disabled={busy}
          >
            {t("workspace.allocation.cancel", "Cancel")}
          </button>

          <button
            type="button"
            className="danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <LoaderCircle size={16} className="lender-invoices-spin" />
            ) : (
              <Trash2 size={15} />
            )}
            {busy
              ? t("workspace.allocation.deleting", "Deleting...")
              : t("workspace.allocation.delete", "Delete allocation")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PREVIEW MODAL
   ========================================================= */

function InvoicePreviewModal({ open, title, fileUrl, onClose, t }) {
  React.useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="lender-invoice-preview" onClick={onClose}>
      <div
        className="lender-invoice-preview__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lender-invoice-preview__header">
          <div>
            <span>{t("workspace.preview.kicker", "Invoice preview")}</span>
            <h3>{title}</h3>
          </div>

          <button type="button" onClick={onClose}>
            {t("workspace.preview.close", "Close")}
          </button>
        </div>

        <div className="lender-invoice-preview__body">
          {fileUrl ? (
            <iframe src={fileUrl} title={title} />
          ) : (
            <div className="lender-invoices-state">
              {t("workspace.preview.unavailable", "Preview is not available.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PAGE
   ========================================================= */

export default function LenderInvoicesPage() {
  const { t } = useTranslation("lender-invoices");
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();

  const [loanCases, setLoanCases] = React.useState([]);
  const [selectedLoanCaseId, setSelectedLoanCaseId] = React.useState(
    searchParams.get("case"),
  );
  const [loanCase, setLoanCase] = React.useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = React.useState(
    searchParams.get("invoice"),
  );

  const [caseSearchInput, setCaseSearchInput] = React.useState("");
  const [caseSearch, setCaseSearch] = React.useState("");

  const [casesLoading, setCasesLoading] = React.useState(true);
  const [caseLoading, setCaseLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [previewTitle, setPreviewTitle] = React.useState("");

  const [allocationModalOpen, setAllocationModalOpen] = React.useState(false);
  const [allocationMode, setAllocationMode] = React.useState("create");
  const [allocationInvoice, setAllocationInvoice] = React.useState(null);
  const [editingAllocation, setEditingAllocation] = React.useState(null);
  const [allocationBusy, setAllocationBusy] = React.useState(false);
  const [busyAllocationId, setBusyAllocationId] = React.useState(null);

  const [deleteAllocationTarget, setDeleteAllocationTarget] =
    React.useState(null);
  const [deleteAllocationBusy, setDeleteAllocationBusy] = React.useState(false);
  const [deleteAllocationError, setDeleteAllocationError] = React.useState("");

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setCaseSearch(caseSearchInput.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [caseSearchInput]);

  const loadLoanCases = React.useCallback(async () => {
    if (authLoading || !isAuthenticated) return;

    try {
      setCasesLoading(true);
      setError("");

      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();

      if (caseSearch) params.set("search", caseSearch);

      const query = params.toString();

      const response = await fetch(
        `${API_BASE}/api/lender/invoice-loan-cases/${query ? `?${query}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || "Could not load lender loan cases.");
      }

      const rawCases = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];

      const mappedAll = rawCases.map(mapLoanCaseFromApi);

      const visibleCases = mappedAll.filter((item) => {
        const caseType = String(item.caseType || "").toUpperCase();

        return !(caseType === "ADDITIONAL" && item.parentLoanCaseId);
      });

      setLoanCases(visibleCases);

      setSelectedLoanCaseId((current) => {
        if (current) {
          const currentCase = mappedAll.find(
            (item) => String(item.loanCaseId) === String(current),
          );

          if (
            currentCase &&
            String(currentCase.caseType || "").toUpperCase() === "ADDITIONAL" &&
            currentCase.parentLoanCaseId
          ) {
            const parentId = currentCase.parentLoanCaseId;

            if (
              visibleCases.some(
                (item) => String(item.loanCaseId) === String(parentId),
              )
            ) {
              return parentId;
            }
          }

          if (
            visibleCases.some(
              (item) => String(item.loanCaseId) === String(current),
            )
          ) {
            return current;
          }
        }

        return visibleCases[0]?.loanCaseId ?? null;
      });
    } catch (err) {
      console.error("Failed to load lender invoice loan cases:", err);
      setLoanCases([]);
      setLoanCase(null);
      setError(err?.message || "Could not load lender loan cases.");
    } finally {
      setCasesLoading(false);
    }
  }, [authLoading, isAuthenticated, getAccessTokenSilently, caseSearch]);

  React.useEffect(() => {
    loadLoanCases();
  }, [loadLoanCases]);

  const loadLoanCase = React.useCallback(async () => {
    if (!isAuthenticated || !selectedLoanCaseId) {
      setLoanCase(null);
      return;
    }

    try {
      setCaseLoading(true);
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/lender/invoice-loan-cases/${selectedLoanCaseId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || "Could not load renovation invoices.");
      }

      const mappedCase = mapLoanCaseFromApi(data);
      setLoanCase(mappedCase);

      setSelectedInvoiceId((current) => {
        if (
          current &&
          mappedCase.invoices.some(
            (invoice) => String(invoice.id) === String(current),
          )
        ) {
          return current;
        }

        return mappedCase.invoices[0]?.id ?? null;
      });
    } catch (err) {
      console.error("Failed to load lender invoice case:", err);
      setLoanCase(null);
      setError(err?.message || "Could not load renovation invoices.");
    } finally {
      setCaseLoading(false);
    }
  }, [selectedLoanCaseId, isAuthenticated, getAccessTokenSilently]);

  React.useEffect(() => {
    loadLoanCase();
  }, [loadLoanCase]);

  React.useEffect(() => {
    const params = new URLSearchParams();

    if (selectedLoanCaseId) params.set("case", selectedLoanCaseId);
    if (selectedInvoiceId) params.set("invoice", selectedInvoiceId);

    setSearchParams(params, { replace: true });
  }, [selectedLoanCaseId, selectedInvoiceId, setSearchParams]);

  const selectedInvoice =
    loanCase?.invoices?.find(
      (invoice) => String(invoice.id) === String(selectedInvoiceId),
    ) ?? null;

  const selectedCaseLoaded =
    loanCase &&
    selectedLoanCaseId &&
    String(loanCase.loanCaseId) === String(selectedLoanCaseId);

  const workspaceLoading =
    casesLoading ||
    Boolean(selectedLoanCaseId && (caseLoading || !selectedCaseLoaded));

  function handleSelectLoanCase(loanCaseId) {
    setSelectedLoanCaseId(loanCaseId);
    setSelectedInvoiceId(null);
  }

  function getVersionFileUrl(version) {
    if (!version?.id) return "";
    return `${API_BASE}/api/lender/invoice-versions/${version.id}/file/`;
  }

  async function handleViewVersion(version, invoice) {
    const fileUrl = getVersionFileUrl(version);
    if (!fileUrl) return;

    try {
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(fileUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Could not load invoice preview.");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setPreviewUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return blobUrl;
      });

      setPreviewTitle(version?.filename || invoice?.filename || "Invoice");
      setPreviewOpen(true);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not load invoice preview.");
    }
  }

  async function handleDownloadVersion(version, invoice) {
    const fileUrl = getVersionFileUrl(version);
    if (!fileUrl) return;

    try {
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(fileUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Could not download invoice.");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = version?.filename || invoice?.filename || "invoice.pdf";

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not download invoice.");
    }
  }

  function openCreateAllocation(invoice) {
    if (!invoice || !loanCase?.fundingSources?.length) return;

    setAllocationMode("create");
    setAllocationInvoice(invoice);
    setEditingAllocation(null);
    setAllocationModalOpen(true);
  }

  function openEditAllocation(allocation, invoice) {
    if (!allocation || !invoice) return;

    setAllocationMode("edit");
    setAllocationInvoice(invoice);
    setEditingAllocation(allocation);
    setAllocationModalOpen(true);
  }

  const closeAllocationModal = React.useCallback(() => {
    if (allocationBusy) return;

    setAllocationModalOpen(false);
    setAllocationInvoice(null);
    setEditingAllocation(null);
  }, [allocationBusy]);

  async function refreshAllocationData() {
    await loadLoanCase();
    await loadLoanCases();
  }

  async function handleSaveAllocation(payload) {
    if (!allocationInvoice?.id) {
      throw new Error("No invoice is selected.");
    }

    try {
      setAllocationBusy(true);
      setError("");

      const token = await getAccessTokenSilently();

      const editing = allocationMode === "edit" && editingAllocation?.id;

      const url = editing
        ? `${API_BASE}/api/lender/invoice-allocations/${editingAllocation.id}/`
        : `${API_BASE}/api/lender/invoice-allocations/invoices/${allocationInvoice.id}/`;

      const response = await fetch(url, {
        method: editing ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw buildApiError(
          data,
          editing
            ? "Could not update funding allocation."
            : "Could not create funding allocation.",
        );
      }

      setAllocationModalOpen(false);
      setAllocationInvoice(null);
      setEditingAllocation(null);

      await refreshAllocationData();
    } catch (error) {
      console.error("Funding allocation save failed:", error);
      throw error;
    } finally {
      setAllocationBusy(false);
    }
  }

  function handleDeleteAllocation(allocation, invoice) {
    if (!allocation?.id) return;

    setDeleteAllocationTarget({ allocation, invoice });
    setDeleteAllocationError("");
  }

  const closeDeleteAllocationModal = React.useCallback(() => {
    if (deleteAllocationBusy) return;

    setDeleteAllocationTarget(null);
    setDeleteAllocationError("");
  }, [deleteAllocationBusy]);

  async function confirmDeleteAllocation() {
    const allocation = deleteAllocationTarget?.allocation;

    if (!allocation?.id || deleteAllocationBusy) return;

    try {
      setDeleteAllocationBusy(true);
      setBusyAllocationId(allocation.id);
      setDeleteAllocationError("");
      setError("");

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/lender/invoice-allocations/${allocation.id}/delete/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw buildApiError(data, "Could not delete funding allocation.");
      }

      setDeleteAllocationTarget(null);
      await refreshAllocationData();
    } catch (error) {
      console.error("Funding allocation delete failed:", error);

      const detailedMessage =
        Array.isArray(error?.details) && error.details.length
          ? error.details
              .map(
                (item) =>
                  `${item.field ? `${item.field}: ` : ""}${item.message}`,
              )
              .join(" ")
          : error?.message || "Could not delete funding allocation.";

      setDeleteAllocationError(detailedMessage);
    } finally {
      setDeleteAllocationBusy(false);
      setBusyAllocationId(null);
    }
  }

  const closePreview = React.useCallback(() => {
    setPreviewOpen(false);

    setPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return "";
    });
  }, []);

  React.useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (authLoading) {
    return (
      <div className="lender-invoices-page">
        <div className="lender-invoices-page-loading">
          <LoaderCircle size={26} className="lender-invoices-spin" />
          {t("workspace.loading", "Loading invoices...")}
        </div>
      </div>
    );
  }

  return (
    <div className="lender-invoices-page">
      <header className="lender-invoices-hero">
        <div>
          <span className="lender-invoices-hero__eyebrow">
            {t("workspace.eyebrow", "Renovation monitoring")}
          </span>

          <h1>{t("workspace.title", "Renovation invoices")}</h1>

          <p>
            {t(
              "workspace.subtitle",
              "Track renovation invoices and expenditure for financed homebuyers, whether or not a renovation project has been created on the platform.",
            )}
          </p>
        </div>
      </header>

      {error ? <div className="lender-invoices-error">{error}</div> : null}

      <FinancialSummary loanCase={loanCase} t={t} />

      <div className="lender-invoices-workspace">
        <LoanCasesPanel
          loanCases={loanCases}
          selectedLoanCaseId={selectedLoanCaseId}
          search={caseSearchInput}
          onSearchChange={setCaseSearchInput}
          onSelect={handleSelectLoanCase}
          loading={casesLoading}
          t={t}
        />

        <InvoiceList
          invoices={selectedCaseLoaded ? (loanCase?.invoices ?? []) : []}
          selectedInvoiceId={selectedInvoiceId}
          onSelect={setSelectedInvoiceId}
          loading={workspaceLoading}
          t={t}
        />

        <InvoiceDetails
          invoice={selectedCaseLoaded ? selectedInvoice : null}
          fundingSources={
            selectedCaseLoaded ? (loanCase?.fundingSources ?? []) : []
          }
          loading={workspaceLoading}
          busyAllocationId={busyAllocationId}
          onViewVersion={handleViewVersion}
          onDownloadVersion={handleDownloadVersion}
          onCreateAllocation={openCreateAllocation}
          onEditAllocation={openEditAllocation}
          onDeleteAllocation={handleDeleteAllocation}
          t={t}
        />
      </div>

      <FundingAllocationModal
        open={allocationModalOpen}
        mode={allocationMode}
        invoice={allocationInvoice}
        fundingSources={loanCase?.fundingSources ?? []}
        allocation={editingAllocation}
        busy={allocationBusy}
        onClose={closeAllocationModal}
        onSubmit={handleSaveAllocation}
        t={t}
      />

      <DeleteAllocationModal
        open={Boolean(deleteAllocationTarget)}
        allocation={deleteAllocationTarget?.allocation ?? null}
        invoice={deleteAllocationTarget?.invoice ?? null}
        fundingSources={loanCase?.fundingSources ?? []}
        busy={deleteAllocationBusy}
        error={deleteAllocationError}
        onClose={closeDeleteAllocationModal}
        onConfirm={confirmDeleteAllocation}
        t={t}
      />

      <InvoicePreviewModal
        open={previewOpen}
        title={previewTitle}
        fileUrl={previewUrl}
        onClose={closePreview}
        t={t}
      />
    </div>
  );
}
